import React, { Dispatch, MutableRefObject, useEffect, useRef, useState } from 'react';
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import VectorLayer from 'ol/layer/Vector.js';
import { fromLonLat } from 'ol/proj.js';
import VectorSource from 'ol/source/Vector.js';
import Fill from 'ol/style/Fill.js';
import Stroke from 'ol/style/Stroke.js';
import Style from 'ol/style/Style.js';
import 'ol/ol.css'
import Feature, { FeatureLike } from 'ol/Feature'
import { defaults as defaultInteractions, Draw } from 'ol/interaction'
import Text from 'ol/style/Text'

import { Geometry, MultiPolygon, Polygon } from 'ol/geom';
import MapSelectorPopup, { AdminData, ALERT_COLORS } from './MapSelectorPopup';
import { EarlyWarningTypes, ResponseGetActiveWarningDto } from './CreateWarningTypes';
import { useTheme } from '../../context/ThemeContext';
import { getThemeColors } from './CommonMethods';
import { ResponseTypes } from '../../services/APIService';
import { text } from 'stream/consumers';
import { features } from 'process';
import { unByKey } from 'ol/Observable';
import { EventsKey } from 'ol/events';
import MapSelectorPolygonPopup from './MapSelectorPolygonPopup';

type MapSelectorPropsType = {
    type: EarlyWarningTypes;
    setData: MutableRefObject<AdminData[]>;
    startDate: string;
    endDate: string;
    //rowState: Dispatch<React.SetStateAction<Record<string, RowState>>>
    tableData: AdminData[];
    setTableData: Dispatch<React.SetStateAction<AdminData[]>>;
    allowedAreas: string[]
}

const MapSelector = (props: MapSelectorPropsType) => {

    const BASE_URL = process.env.REACT_APP_BASE_URL

    //const [oldAlert, setOldAlert] = useState<ResponseGetActiveWarningDto[]>([])
    const theme = useTheme()

    const mapRef = useRef<HTMLDivElement>(null)
    const mapObjRef = useRef<Map | null>(null)
    const selectedFeaturesRef = useRef<Feature<Geometry>[]>([])
    const [selectedFeatures, setSelectedFeatures] = useState<FeatureLike[]>([])
    const [tempSelectedFeature, setTempSelectedFeature] = useState<FeatureLike[]>([])

    const [latestSelect, setLatestSelect] = useState<FeatureLike | null>(null)
    const [popupStatus, setPopupStatus] = useState<boolean>(false)

    const [vectorLayers, setVectorLayers] = useState<VectorLayer<VectorSource>[]>([])

    const [lastSelected, setLastSelected] = useState<FeatureLike | null>(null)

    const [adminLevel, setAdminLevel] = useState<string>('')

    const [polygonModeEnabled, setPolygonModeEnabled] = useState<boolean>(false)

    const getThemeColorss = (): { background: string; stroke: string; defaultFill: string } => {
        switch (theme.theme) {
            case 'dark':
                return {
                    background: '#1a1a1a',
                    stroke: 'black',
                    defaultFill: '#EEE'
                }
            case 'light':
                return {
                    background: '#f5f5f5',
                    stroke: 'black',
                    defaultFill: '#EEE'
                }
            case 'colorful':
            default:
                return {
                    background: '#e8f4f8',
                    stroke: 'black',
                    defaultFill: '#EEE'
                }
        }
    }
    
    const getAlertColor = (alertLevel: string): string => {
        const key = alertLevel.toLowerCase() as keyof typeof ALERT_COLORS;
        return ALERT_COLORS[key] || getThemeColorss().defaultFill
    }
    
    // Handle deselect method
    const handleDeselectFeature = (feature: FeatureLike | null) => {
        if (!feature || !mapObjRef.current) return;

        const colors = getThemeColors(theme);
        const f = feature as Feature<Geometry>;
        const map = mapObjRef.current;

        const adminCode = f.get('ADM3_PCODE') || f.get('ADM2_PCODE') || f.get('ADM1_PCODE') || f.get('ADM0_PCODE');

        // Reset style immediately
        f.setStyle(
            new Style({
                fill: new Fill({ color: colors.defaultFill }),
                stroke: new Stroke({ color: colors.stroke, width: 1 }),
            })
        );
        f.set('interactive', true);

        f.setProperties({
            TYPE: '',
            PREV_ALERT: '',
            NEW_ALERT: '',
            RAINFALL: '',
            LIGHTNING_RISK: '',
            MIN_WIND: '',
            MAX_WIND: '',
            SEVERITY: '',
            LIGHTNING: '',
            START_DATE: '',
            END_DATE: '',
            MAX_WAVE: '',
            MIN_WAVE: ''
        });

        // Reset child layers
        if (adminCode) {
            const allLayers = map.getLayers().getArray().reverse();

            allLayers.forEach(layer => {
                if (layer instanceof VectorLayer) {
                    const source = layer.getSource();
                    if (!source) return;

                    source.getFeatures().forEach((layerFeature: Feature<Geometry>) => {
                        const layerFeatureAdminCode = layerFeature.get('ADM3_PCODE') || layerFeature.get('ADM2_PCODE') || layerFeature.get('ADM1_PCODE') || layerFeature.get('ADM0_PCODE') || ''

                        if (layerFeatureAdminCode.startsWith(adminCode)) {
                            layerFeature.setStyle(
                                new Style({
                                    fill: new Fill({ color: colors.defaultFill }),
                                    stroke: new Stroke({ color: colors.stroke }),
                                })
                            );
                            layerFeature.set('interactive', true);
                        }
                    });
                }
            });
        }

        // Update selected features state
        setSelectedFeatures(prevSelected =>
            prevSelected.filter(data => data !== feature)
        );

        // Remove from table
        if (adminCode) {
            props.setTableData(prevState =>
                prevState.filter(item => item.data.code !== adminCode)
            );
        }

        setPopupStatus(false);
    }

    const handleDeselectFeatureMulti = (feature: FeatureLike[]) => {
        if (!feature || !mapObjRef.current) return;

        const colors = getThemeColors(theme);
        const f = feature as Feature<Geometry>[];
        const map = mapObjRef.current;

        f.forEach(item => {
            const adminCode = item.get('ADM3_PCODE') || item.get('ADM2_PCODE') || item.get('ADM1_PCODE') || item.get('ADM0_PCODE');

            // Reset style immediately
            item.setStyle(
                new Style({
                    fill: new Fill({ color: colors.defaultFill }),
                    stroke: new Stroke({ color: colors.stroke, width: 1 }),
                })
            );
            item.set('interactive', true);

            item.setProperties({
                TYPE: '',
                PREV_ALERT: '',
                NEW_ALERT: '',
                RAINFALL: '',
                LIGHTNING_RISK: '',
                MIN_WIND: '',
                MAX_WIND: '',
                SEVERITY: '',
                LIGHTNING: '',
                START_DATE: '',
                END_DATE: '',
                MAX_WAVE: '',
                MIN_WAVE: ''
            });

            // Reset child layers
            if (adminCode) {
                const allLayers = map.getLayers().getArray().reverse();

                allLayers.forEach(layer => {
                    if (layer instanceof VectorLayer) {
                        const source = layer.getSource();
                        if (!source) return;

                        source.getFeatures().forEach((layerFeature: Feature<Geometry>) => {
                            const layerFeatureAdminCode = layerFeature.get('ADM3_PCODE') || layerFeature.get('ADM2_PCODE') || layerFeature.get('ADM1_PCODE') || layerFeature.get('ADM0_PCODE') || ''

                            if (layerFeatureAdminCode.startsWith(adminCode)) {
                                layerFeature.setStyle(
                                    new Style({
                                        fill: new Fill({ color: colors.defaultFill }),
                                        stroke: new Stroke({ color: colors.stroke }),
                                    })
                                );
                                layerFeature.set('interactive', true);
                            }
                        });
                    }
                });
            }

            // Update selected features state
            setSelectedFeatures(prevSelected =>
                prevSelected.filter(data => data !== item)
            );

            // Remove from table
            if (adminCode) {
                props.setTableData(prevState =>
                    prevState.filter(item => item.data.code !== adminCode)
                );
            }
        })
        setPopupStatus(false);
    }


    // Function to update map styling based on theme
    const updateMapTheme = () => {
        if (!mapObjRef.current) return
        
        const colors = getThemeColors(theme)
        
        // Update map container background
        if (mapRef.current) {
            mapRef.current.style.backgroundColor = colors.background
        }
        
        // Update all vector layers
        vectorLayers.forEach(layer => {
            layer.setBackground(colors.background)
            const currentStyle = layer.getStyle()
            
            if (typeof currentStyle === 'function') {
                layer.setStyle((feature) => {
                    return new Style({
                        fill: new Fill({ color: colors.defaultFill }),
                        stroke: new Stroke({ 
                            color: colors.stroke,
                            width: 1 
                        }),
                    })
                })
            }
        })
    }

    const loadAdminLevel = async (file: 'LK_SEA' | 'LK_ADM0' | 'LK_ADM1' | 'LK_ADM2' | 'LK_ADM3' | 'MV_SEA' | 'MV_ADM0' | 'MV_ADM1' | 'MV_ADM2'): Promise<VectorLayer<VectorSource>> => {

        const colors = getThemeColors(theme)
        const vectorSource = new VectorSource()

        let url = ''
        switch (file) {
            case 'LK_SEA': url = '/geoJSON/lk/lk_admin_sea_deff.json'; break
            case 'LK_ADM0': url = '/geoJSON/lk/lk_admin_0.json'; break
            case 'LK_ADM1': url = '/geoJSON/lk/lk_admin_1.json'; break
            case 'LK_ADM2': url = '/geoJSON/lk/lk_admin_2.json'; break
            case 'LK_ADM3': url = '/geoJSON/lk/lk_admin_3.json'; break
            case 'MV_SEA': url = '/geoJSON/maldives/maldives_sea_dff.json'; break
            case 'MV_ADM0': url = '/geoJSON/maldives/maldives_adm_0.json'; break
            case 'MV_ADM1': url = '/geoJSON/maldives/maldives_adm_1.json'; break
            case 'MV_ADM2': url = '/geoJSON/maldives/maldives_adm_2.json'; break
        }

        // await the fetch
        const response = await fetch(url)
        const jsonFile = await response.json()

        const features = new GeoJSON().readFeatures(jsonFile, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
        });

        // Store original colors
        features.forEach(feature => {
            const originalColor = feature.get('COLOR') || colors.defaultFill
            feature.set('ORIGINAL_COLOR', originalColor)
            feature.set('COLOR', originalColor)
            feature.set('interactive', true)
        })

        vectorSource.addFeatures(features)

        return new VectorLayer({
            source: vectorSource,
            background: colors.background,
            style: (feature) => {
                const color = feature.get('COLOR') || feature.get('ORIGINAL_COLOR') || colors.defaultFill
                console.log("COLOR: ", color)
                let labelGeometry;
                const geometry = feature.getGeometry();
                if (geometry instanceof Polygon) {
                    labelGeometry = geometry.getInteriorPoint();
                } else if (geometry instanceof MultiPolygon) {
                    const polygons = geometry.getPolygons();
                    if (polygons.length > 0) labelGeometry = polygons[0].getInteriorPoint();
                }

                /* const textLabelStyle = new Text({
                    text: feature.get('ADM2_EN') || feature.get('ADM1_EN') || feature.get('ADM0_EN') || 'N/A',
                    font: '12px Calibri,sans-serif',
                    fill: new Fill({ color: '#000' }),
                    stroke: new Stroke({ color: '#fff', width: 2 }),
                    overflow: true,
                    textAlign: 'center',
                    textBaseline: 'middle'
                }) */

                return new Style({
                    fill: new Fill({ color }),
                    stroke: new Stroke({ 
                        color: colors.stroke,
                        width: 1 
                    }),
                    //text: textLabelStyle,
                    //geometry: labelGeometry
                });
            },
        })
    }

    
    useEffect(() => {
        if (!mapRef.current) return

        let map: Map;
        let initialView: View;

        const initMap = async () => {
            const colors = getThemeColors(theme);
            const country = process.env.REACT_APP_COUNTRY;

            let layers: VectorLayer<VectorSource>[] = [];

            if (country === "SRI_LANKA") {
                if (props.type === 'MWA') {
                    layers = await Promise.all([
                        loadAdminLevel('LK_SEA'),
                        loadAdminLevel('LK_ADM0')
                    ]);
                } 
                else if (props.type === "TW" || props.type === "GUS")
                {
                    layers = await Promise.all([
                        loadAdminLevel('LK_SEA'),
                        loadAdminLevel('LK_ADM0'),
                        loadAdminLevel('LK_ADM1'),
                        loadAdminLevel('LK_ADM2'),
                        loadAdminLevel('LK_ADM3')
                    ]);
                }
                else {
                    layers = await Promise.all([
                        //loadAdminLevel('LK_SEA'),
                        loadAdminLevel('LK_ADM0'),
                        loadAdminLevel('LK_ADM1'),
                        loadAdminLevel('LK_ADM2'),
                        loadAdminLevel('LK_ADM3')
                    ]);
                }

                initialView = new View({
                    center: fromLonLat([79.8612, 6.9271]),
                    zoom: 7
                });

            } else if (country === "MALDIVES") {
                if (props.type === "MWA") {
                    layers = await Promise.all([
                        loadAdminLevel('MV_SEA'),
                        loadAdminLevel('MV_ADM0')
                    ]);
                }
                else if (props.type === "TW" || props.type === "GUS")
                {
                    layers = await Promise.all([
                        loadAdminLevel('MV_SEA'),
                        loadAdminLevel('MV_ADM0'),
                        loadAdminLevel('MV_ADM1'),
                        loadAdminLevel('MV_ADM2')
                        
                    ]);
                }
                else {
                    layers = await Promise.all([
                        loadAdminLevel('MV_ADM0'),
                        loadAdminLevel('MV_ADM1'),
                        loadAdminLevel('MV_ADM2')
                    ]);
                }

                initialView = new View({
                    center: fromLonLat([73.2207, 3.2028]),
                    zoom: 7,
                    maxZoom: 8
                });
            } else {
                return;
            }

            /* const vectorSource = new VectorSource()
            const vectorLayer = new VectorLayer({
                source: vectorSource
            }) */

            map = new Map({
                target: mapRef.current!,
                //layers: [...layers, vectorLayer],
                layers: layers,
                view: initialView,
                interactions: defaultInteractions({
                    doubleClickZoom: false,
                    mouseWheelZoom: true
                })
            });

            mapObjRef.current = map;

            /* const draw = new Draw({
                source: vectorSource,
                type: "Polygon",
            });
            mapObjRef.current.addInteraction(draw);

            // Optional: log polygon coordinates when finished
            draw.on("drawend", (evt: any) => {
                const coords = evt.feature.getGeometry().getCoordinates();
                console.log("Polygon coordinates:", coords);
            }); */

            setVectorLayers(layers);

            if (mapRef.current) {
                mapRef.current.style.backgroundColor = colors.background;
            }

            // Initial layer visibility
            const setInitialVisibility = () => {
                if (country === "SRI_LANKA") {
                    if (props.type === 'MWA') {
                        if (layers[0]) layers[0].setVisible(true);
                        if (layers[1]) {
                            layers[1].setVisible(true);
                            layers[1].set('interactive', false);
                            layers[1].setStyle(new Style({ 
                                fill: new Fill({ color: colors.defaultFill }), 
                                stroke: new Stroke({ color: colors.stroke, width: 1 }) 
                            }));
                        }
                    } 
                    else if (props.type === "TW" || props.type === "GUS") {
                        if (layers[0]) layers[0].setVisible(true);
                        if (layers[1]) layers[1].setVisible(true);
                        if (layers[2]) layers[2].setVisible(false);
                        if (layers[3]) layers[3].setVisible(false);
                        if (layers[4]) layers[4].setVisible(false);
                    }
                    else {
                        if (layers[0]) layers[0].setVisible(false);
                        if (layers[1]) layers[1].setVisible(true);  // This should exist - LK_ADM1
                        if (layers[2]) layers[2].setVisible(false); // This should exist - LK_ADM2
                        if (layers[3]) layers[3].setVisible(false); // This should exist - LK_ADM3
                    }
                    setAdminLevel("Province Level")
                } else if (country === "MALDIVES") {
                    if (props.type === 'MWA') {
                        layers[0].setVisible(true);
                        layers[0].set('interactive', true);
                        layers[1].setVisible(true);
                        layers[1].set('interactive', false);
                        layers[1].setStyle(new Style({
                            fill: new Fill({ color: colors.defaultFill })
                        }))
                    }
                    else if (props.type === "TW" || props.type === "GUS")
                    {
                        layers[0].setVisible(true)
                        layers[1].setVisible(true)
                        layers[2].setVisible(false)
                        layers[3].setVisible(false)
                    }
                    else {
                        layers[0].setVisible(false)
                        layers[1].setVisible(true)
                        layers[2].setVisible(false)
                        //layers[3].setVisible(false);
                    }

                    setAdminLevel("Atoll Level")
                }
            };
            //const type = "HRW"
            
            setInitialVisibility();

            //map.on("singleclick", (e) => handleMapOnClickEvent(e))

            // Zoom listener
            const onZoomChange = () => {
                const zoom = initialView.getZoom()
                if (zoom === undefined) return
                //console.log("ZOOM -> ", zoom)

                if (country === "SRI_LANKA") {
                    if (zoom < 8) {
                        if (props.type === 'MWA') {
                            if (layers[0]) layers[0].setVisible(true)
                            if (layers[1]) layers[1].setVisible(true)
                        } else {
                            if (layers[0]) layers[0].setVisible(false)
                            if (layers[1]) layers[1].setVisible(true)
                            if (layers[2]) layers[2].setVisible(false)
                            if (layers[3]) layers[3].setVisible(false)
                            if (layers[4]) layers[4].setVisible(false)
                        }
                        setAdminLevel("Province Level")
                    } else if (zoom >= 8 && zoom < 9) {
                        if (props.type === 'MWA') {
                            if (layers[0]) layers[0].setVisible(true)
                            if (layers[1]) layers[1].setVisible(true)
                        } else {
                            if (layers[0]) layers[0].setVisible(false)
                            if (layers[1]) layers[1].setVisible(false)
                            if (layers[2]) layers[2].setVisible(false)
                            if (layers[3]) layers[3].setVisible(true)
                            if (layers[4]) layers[4].setVisible(false)
                        }
                        setAdminLevel("District Level")
                    } else if (zoom >= 9 && zoom < 10) {
                        if (props.type === 'MWA') {
                            if (layers[0]) layers[0].setVisible(true)
                            if (layers[1]) layers[1].setVisible(true)
                        } else {
                            if (layers[0]) layers[0].setVisible(false)
                            if (layers[1]) layers[1].setVisible(false)
                            if (layers[2]) layers[2].setVisible(false)
                            if (layers[3]) layers[3].setVisible(false)
                            if (layers[4]) layers[4].setVisible(true)
                        }
                        setAdminLevel("GN Level")
                    }
                }
                else if (country === "MALDIVES") 
                {
                    if (zoom < 7) {
                        if (props.type === 'MWA') {
                            layers[0].setVisible(true)
                            layers[1].setVisible(true)
                        }
                        else if (props.type === "TW" || props.type === "GUS")
                        {
                            layers[0].setVisible(true)
                            layers[1].setVisible(true)
                            layers[2].setVisible(false)
                            layers[3].setVisible(false)
                        }
                        else {
                            layers[0].setVisible(true)
                            layers[1].setVisible(false)
                            layers[2].setVisible(false)
                            //layers[3].setVisible(false)
                        }

                        setAdminLevel("Country Level")
                    } else if (zoom >= 7 && zoom < 9) {
                        if (props.type === 'MWA') {
                            layers[0].setVisible(true)
                            layers[1].setVisible(true)
                        } 
                        else if (props.type === "TW" || props.type === "GUS")
                        {
                            layers[0].setVisible(false)
                            layers[1].setVisible(false)
                            layers[2].setVisible(true)
                            layers[3].setVisible(false)
                        }
                        else {
                            layers[0].setVisible(false)
                            layers[1].setVisible(true)
                            layers[2].setVisible(false)
                            //layers[3].setVisible(false)
                        }

                        setAdminLevel("Atoll Level")
                    } else if (zoom >= 9 && zoom < 15) {
                        if (props.type === 'MWA') {
                            layers[0].setVisible(true)
                            layers[1].setVisible(true)
                        }
                        else if (props.type === "TW" || props.type === "GUS")
                        {
                            layers[0].setVisible(false)
                            layers[1].setVisible(false)
                            layers[2].setVisible(false)
                            layers[3].setVisible(true)
                        } 
                        else {
                            layers[0].setVisible(false)
                            layers[1].setVisible(false)
                            layers[2].setVisible(true)
                            //layers[3].setVisible(true)
                        }
                        setAdminLevel("Island Level")
                    } else {
                        if (props.type === 'MWA') {
                            layers[0].setVisible(true)
                            layers[1].setVisible(true)
                        } 
                        else if (props.type === "TW" || props.type === "GUS")
                        {
                            layers[0].setVisible(false)
                            layers[1].setVisible(false)
                            layers[2].setVisible(false)
                            layers[3].setVisible(true)
                        }
                        else {
                            layers[0].setVisible(false)
                            layers[1].setVisible(false)
                            layers[2].setVisible(true)
                            //layers[3].setVisible(true)
                        }
                    }
                }
            };

            initialView.on('change:resolution', onZoomChange);
        };

        initMap();

        // Cleanup
        return () => {
            mapObjRef.current?.getView()?.un('change:resolution', () => {});
            mapObjRef.current?.un('singleclick', () => {});
            mapObjRef.current?.setTarget(undefined);
            mapObjRef.current = null;
        }
    }, [])

    useEffect(() => {
        updateMapTheme();
    }, [theme])

    useEffect(() => {
        
        const newSelect = selectedFeatures.filter(feature => !tempSelectedFeature.includes(feature))
        
        const code: string[] = selectedFeatures.map((feature) => {
            return feature.getProperties()['ADM3_PCODE'] || feature.getProperties()['ADM2_PCODE'] || feature.getProperties()['ADM1_PCODE'] || feature.getProperties()['ADM0_PCODE']
        })

        /* props.setData.current = props.setData.current.filter((item) => {
            return code.includes(item.data.code)
        })
        console.log("filtered: ", props.setData.current) */

        if (newSelect.length > 0) {
            setLatestSelect(newSelect[0])
            setPopupStatus(true)
        } else {
            setLatestSelect(null)
            //setPopupStatus(false)
        }

        setTempSelectedFeature(selectedFeatures)
    }, [selectedFeatures])


    const updateTableMap = (type: EarlyWarningTypes) => {
        if (!mapObjRef.current || !vectorLayers) return
            vectorLayers.forEach(layer => {
                if (!(layer instanceof VectorLayer)) return
                    if (layer instanceof VectorLayer) 
                    {
                        const source = layer.getSource()
                        if (!source) return
                        const features = source.getFeatures()

                        features.forEach((feature: Feature<Geometry>) => {
                        // Get the feature's admin code
                        const areaCode: string | undefined = feature.get('ADM3_PCODE') || feature.get('ADM2_PCODE') || feature.get('ADM1_PCODE') || feature.get('ADM0_PCODE') || feature.get('ADM_PCODE')

                        if (!areaCode) return;
                        // Find matching warning
                        const warning = props.tableData.find(w => w.type === type && w.data.code === areaCode)
                        //console.log("---->> DATA: ", props.tableData)
                        if (!warning) return
                        
                        switch (type){
                            case "LSW": {
                                const selectWarning = warning.type === "LSW" ? warning.data : undefined
                                if (selectWarning === undefined) return
                                if (selectWarning.previousAlertLevel !== "" && selectWarning.newAlertLevel === "") {
                                    const color = getAlertColor(selectWarning.previousAlertLevel)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                    })
                                } else {
                                    const color = getAlertColor(selectWarning.newAlertLevel)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                        NEW_ALERT: selectWarning.newAlertLevel,
                                        RAINFALL: selectWarning.rainfall,
                                        LIGHTNING_RISK: selectWarning.lightingRiskLevel,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "FLW": {
                                const selectWarning = warning.type === "FLW" ? warning.data : undefined
                                if (selectWarning === undefined) return

                                if (selectWarning.previousAlertLevel !== "" && selectWarning.newAlertLevel === ""){
                                    const color = getAlertColor(selectWarning.previousAlertLevel)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                    })
                                } else {
                                    const color = getAlertColor(selectWarning.newAlertLevel)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                        NEW_ALERT: selectWarning.newAlertLevel,
                                        RAINFALL: selectWarning.rainfall,
                                        LIGHTNING_RISK: selectWarning.lightingRiskLevel,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "HRW": {
                                const selectWarning = warning.type === "HRW" ? warning.data : undefined
                                if (selectWarning === undefined) return
                                
                                if (selectWarning.previousAlertLevel !== "" && selectWarning.newAlertLevel === ""){
                                    const color = getAlertColor(selectWarning.previousAlertLevel)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                    })
                                } else {
                                    const color = getAlertColor(selectWarning.newAlertLevel)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                        NEW_ALERT: selectWarning.newAlertLevel,
                                        RAINFALL: selectWarning.rainfall,
                                        LIGHTNING_RISK: selectWarning.lightingRiskLevel,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "HWN": {
                                const selectWarning = warning.type === "HWN" ? warning.data : undefined
                                if (selectWarning === undefined) return
                                if (selectWarning.prevSeverity !== "" && selectWarning.severity === ""){
                                    const color = getAlertColor(selectWarning.prevSeverity)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_SEVERITY: selectWarning.prevSeverity,
                                    })

                                } else {
                                    const color = getAlertColor(selectWarning.severity)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        MIN_WIND: selectWarning.minWindSpeed,
                                        MAX_WIND: selectWarning.maxWindSpeed,
                                        PREV_SEVERITY: selectWarning.prevSeverity,
                                        SEVERITY: selectWarning.severity,
                                        LIGHTNING: selectWarning.lightingRiskLevel,
                                        //LIGHTNING_RISK: selectWarning.lightingRiskLevel,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                
                                break
                            }
                            case "LIG": {
                                const selectWarning = warning.type === "LIG" ? warning.data : undefined
                                if (selectWarning === undefined) return

                                if (selectWarning.previousAlertLevel !== "" && selectWarning.newAlertLevel === ""){
                                    const color = getAlertColor(selectWarning.previousAlertLevel)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                    })

                                } else {
                                    const color = getAlertColor(selectWarning.newAlertLevel)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_ALERT: selectWarning.previousAlertLevel,
                                        NEW_ALERT: selectWarning.newAlertLevel,
                                        LIGHTNING_RISK: selectWarning.lightingRiskLevel,
                                        LIGHTNING: selectWarning.lightingRiskLevel,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "MWA": {

                                const selectWarning = warning.type === "MWA" ? warning.data : undefined
                                if (selectWarning === undefined) return

                                if (selectWarning.prevSeaCondition !== "" && selectWarning.seaCondition === ""){
                                    const color = getAlertColor(selectWarning.prevSeaCondition)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_SEA_CONDITION: selectWarning.prevSeaCondition,
                                    })

                                } else {
                                    const color = getAlertColor(selectWarning.seaCondition)
                                    
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        MIN_WAVE_HEIGHT: selectWarning.minWave,
                                        MAX_WAVE_HEIGHT: selectWarning.maxWave,
                                        MIN_WIND: selectWarning.minWindSpeed,
                                        MAX_WIND: selectWarning.maxWindSpeed,
                                        PREV_SEA_CONDITION: selectWarning.prevSeaCondition,
                                        SEA_CONDITION: selectWarning.seaCondition,
                                        LIGHTNING: selectWarning.thunderstorm,
                                        LIGHTNING_RISK: selectWarning.thunderstorm,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "TW": {
                                const selectWarning = warning.type === "TW" ? warning.data : undefined
                                if (selectWarning === undefined) return

                                if (selectWarning.prevSeaCondition !== "" && selectWarning.seaCondition === ""){
                                    const color = getAlertColor(selectWarning.prevSeaCondition)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_SEA_CONDITION: selectWarning.prevSeaCondition,
                                    })

                                } else {
                                    const color = getAlertColor(selectWarning.seaCondition)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        MIN_WAVE_HEIGHT: selectWarning.minWave,
                                        MAX_WAVE_HEIGHT: selectWarning.maxWave,
                                        MIN_WIND: selectWarning.minWindSpeed,
                                        MAX_WIND: selectWarning.maxWindSpeed,
                                        PREV_SEA_CONDITION: selectWarning.prevSeaCondition,
                                        SEA_CONDITION: selectWarning.seaCondition,
                                        LIGHTNING: selectWarning.thunderstorm,
                                        LIGHTNING_RISK: selectWarning.thunderstorm,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "CUS": {
                                const selectWarning = warning.type === "CUS" ? warning.data : undefined
                                if (selectWarning === undefined) return

                                if (selectWarning.prevSeverity !== "" && selectWarning.severity === ""){
                                    const color = getAlertColor(selectWarning.prevSeverity)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_SEVERITY: selectWarning.prevSeverity,
                                    })
                                } else {
                                    const color = getAlertColor(selectWarning.severity)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_SEVERITY: selectWarning.prevSeverity,
                                        SEVERITY: selectWarning.severity,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                break
                            }
                            case "ASW": {
                                const selectWarning = warning.type === "ASW" ? warning.data : undefined
                                if (selectWarning === undefined) return
                                const color = getAlertColor(selectWarning.previousAlertLevel)

                                feature.setStyle(
                                    new Style({
                                        fill: new Fill({ color }),
                                        stroke: new Stroke({ color: 'red', width: 2 }),
                                    })
                                )

                                feature.setProperties({
                                    TYPE: type,
                                    PREV_ALERT: selectWarning.previousAlertLevel,
                                    NEW_ALERT: selectWarning.newAlertLevel,
                                    START_DATE: selectWarning.startDate,
                                    END_DATE: selectWarning.endDate
                                })
                                break
                            }
                            case "NTW": {
                                const selectWarning = warning.type === "NTW" ? warning.data : undefined
                                if (selectWarning === undefined) return
                                const color = getAlertColor(selectWarning.name)

                                feature.setStyle(
                                    new Style({
                                        fill: new Fill({ color }),
                                        stroke: new Stroke({ color: 'red', width: 2 }),
                                    })
                                )

                                feature.setProperties({
                                    TYPE: type,
                                    //PREV_ALERT: selectWarning.previousAlertLevel,
                                    //NEW_ALERT: selectWarning.newAlertLevel,
                                    START_DATE: selectWarning.startDate,
                                    END_DATE: selectWarning.endDate
                                })
                                break
                            }
                            case "GUS": {
                                const selectWarning = warning.type === "GUS" ? warning.data : undefined
                                if (selectWarning === undefined) return
                                if (selectWarning.prevSeverity !== "" && selectWarning.severity === ""){
                                    const color = getAlertColor(selectWarning.prevSeverity)
                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )
                                    feature.setProperties({
                                        TYPE: type,
                                        PREV_SEVERITY: selectWarning.prevSeverity,
                                    })

                                } else {
                                    const color = getAlertColor(selectWarning.severity)

                                    feature.setStyle(
                                        new Style({
                                            fill: new Fill({ color }),
                                            stroke: new Stroke({ color: 'red', width: 2 }),
                                        })
                                    )

                                    feature.setProperties({
                                        TYPE: type,
                                        MIN_WIND: selectWarning.minWindSpeed,
                                        MAX_WIND: selectWarning.maxWindSpeed,
                                        PREV_SEVERITY: selectWarning.prevSeverity,
                                        SEVERITY: selectWarning.severity,
                                        LIGHTNING: selectWarning.lightingRiskLevel,
                                        //LIGHTNING_RISK: selectWarning.lightingRiskLevel,
                                        START_DATE: selectWarning.startDate,
                                        END_DATE: selectWarning.endDate
                                    })
                                }
                                
                                break
                            }
                        }
                    })
                }
            })
    }

    /* useEffect(() => {
        if (!mapObjRef.current || !vectorLayers) return;

        if (o`ldAlert && oldAlert.length > 0)
        {
            vectorLayers.forEach(layer => {
                if (!(layer instanceof VectorLayer)) return;
                if (layer instanceof VectorLayer) {
                    const source = layer.getSource();
                    if (!source) return;

                    const features = source.getFeatures();

                    features.forEach((feature: Feature<Geometry>) => {
                    // Get the feature's admin code
                    const areaCode: string | undefined =
                        feature.get('ADM3_PCODE') ||
                        feature.get('ADM2_PCODE') ||
                        feature.get('ADM1_PCODE') ||
                        feature.get('ADM0_PCODE');

                    if (!areaCode) return;

                    // Find matching warning
                    const warning = oldAlert.find(w => w.areaCode === areaCode);
                    if (!warning) return;

                    // Determine color based on status
                    const color = getAlertColor(warning.status);

                    // Apply style
                    feature.setStyle(
                        new Style({
                        fill: new Fill({ color }),
                        stroke: new Stroke({ color: 'red', width: 2 }),
                        })
                    );

                    // Store status in feature properties (optional)
                    feature.set('PREV_ALERT', warning.status);
                    });
                }
            })
        }

    }, [oldAlert]) */

    useEffect(() => {
        
        switch (props.type){
            case "LSW":
                updateTableMap("LSW")
                break
            case "FLW":
                updateTableMap("FLW")
                break
            case "HRW":
                updateTableMap("HRW")
                break
            case "HWN":
                updateTableMap("HWN")
                break
            case "LIG":
                updateTableMap("LIG")
                break
            case "MWA":
                updateTableMap("MWA")
                break
            case "TW":
                updateTableMap("TW")
                break
            case "CUS":
                updateTableMap("CUS")
                break
            case "GUS":
                updateTableMap("GUS")
                break
        }
        
    }, [props.tableData, selectedFeatures, mapObjRef.current, vectorLayers])

    const handleMapOnClickEvent = (e: any) => {
        if (!mapObjRef.current) return

        const colors = getThemeColors(theme);
        const map = mapObjRef.current

        const clickedFeatures: Feature<Geometry>[] = []

        map.forEachFeatureAtPixel(e.pixel, (featureLike, layer) => {
            const feature = featureLike as Feature;
            if (layer?.get('interactive') === false || feature?.get('interactive') === false) return;
            clickedFeatures.push(feature);
        })

        //console.log("SINGLE CLICK >>>>>>> CLICKED FEATURE: ", clickedFeatures)
        if (clickedFeatures.length <= 0) return

        //console.log("SINGLE CLICK >>>>>>> SELECTED FEATURE STATE: ", selectedFeatures)
        
        setSelectedFeatures(prevState => {
            const newSelected = [...prevState]

            clickedFeatures.forEach(feature => {
                //console.log("SINGLE CLICK >>>>>>> CLICKED FEATURES: ", feature)
                const alreadySelected = prevState.includes(feature)
                //console.log("SINGLE CLICK >>>>>>> IS ALREADY SELECTED: ", alreadySelected)

                if (!alreadySelected)
                {
                    //console.log("SINGLE CLICK >>>>>>> NOT ALREADY SELECTED:")
                    feature.setStyle(new Style({
                        fill: new Fill({
                            color: colors.defaultFill
                        }),
                        stroke: new Stroke({ color: colors.stroke, width: 1 }),
                    }))

                    const selectedFeatureCode = feature.get('ADM3_PCODE') || feature.get('ADM2_PCODE') || feature.get('ADM1_PCODE') || feature.get('ADM0_PCODE')
                    //console.log("SELECTED FUTURE CODE -> ", selectedFeatureCode)

                    if (selectedFeatureCode)
                    {
                        const allLayers = map.getLayers().getArray().reverse()
                        //console.log("SINGLE CLICK >>>>>>> LAYERS:", allLayers)

                        const targetLayerIndex = allLayers.findIndex(layer => {
                            if (layer instanceof VectorLayer && layer.getVisible()) {
                                const source = layer.getSource();
                                return source.getFeatures().includes(feature);
                            }
                            return false;
                        })
                        //console.log("SINGLE CLICK >>>>>>> SELECTED LAYER INDEX:", targetLayerIndex)
                        //console.log("SINGLE CLICK >>>>>>> LAYER LENGTH:", allLayers.length)
                        allLayers.forEach((layer, index) => {
                            //console.log("INDEX: ", index)
                            //console.log("TARGET LAYERS: ", targetLayerIndex)
                            //if (index > targetLayerIndex) return

                            if (layer instanceof VectorLayer)
                            {
                                //console.log("SINGLE CLICK >>>>>>> CHILD LAYERS:", layer)
                                const source = layer.getSource();
                                const layerFeatures: Feature<Geometry>[] = source.getFeatures() || []
                                
                                if (layerFeatures.length === 0) return

                                layerFeatures.forEach((layerFeature, index) => {
                                    const layerFeatureAdminCode: string = layerFeature.get('ADM3_PCODE') || layerFeature.get('ADM2_PCODE') || layerFeature.get('ADM1_PCODE') || layerFeature.get('ADM0_PCODE')


                                    if (layerFeatureAdminCode && layerFeatureAdminCode.startsWith(selectedFeatureCode))
                                    {
                                        if (layerFeatureAdminCode === selectedFeatureCode) 
                                        {
                                            layerFeature.set('interactive', true)
                                        } 
                                        else 
                                        {
                                            //console.log("DISABLED ----------- ", layerFeatureAdminCode)
                                            layerFeature.setStyle(new Style({
                                                fill: new Fill({color: colors.disable}),
                                                stroke: new Stroke({ color: colors.disableStroke, width: 1 })
                                            }))
                                            layerFeature.set('interactive', false)
                                            //console.log("SINGLE CLICK >>>>>>> LAYER FEATURE CODE RELATED:", layerFeatureAdminCode)
                                        }
                                        
                                    }
                                    //console.log("SINGLE CLICK >>>>>>> LAYER FEATURE:", layerFeatureAdminCode)
                                })
                            }
                        })
                        
                    } /* else {
                        console.log("OUT >>>>>>> ", selectedFeatureCode)
                    } */
                    newSelected.push(feature)
                    //console.log("SINGLE CLICK >>>>>>> SELECTED FEATURE ADMIN CODE:", selectedFeatureCode)
                }
                else 
                {
                    //console.log("SINGLE CLICK >>>>>>> ALREADY SELECTED:")
                    setLastSelected(feature)
                    setPopupStatus(true)
                }
            })
            //console.log("SINGLE CLICK >>>>>>> SELECTED FEATURE STATE: ", newSelected)
            return newSelected
        })

    }

    const clickListenerRef = useRef<EventsKey | null>(null);
    const drawInteractionRef = useRef<Draw | null>(null);
    const polygonLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
    const [polygonPopup, setPolygonPopup] = useState<boolean>(false)

    const [coordinates, setCoordinates] = useState<Feature<Geometry>[]>([])
    const rowCoordinateRef = useRef<[number, number][][]>([])
    useEffect(() => {
        if (!mapObjRef.current) return
        const map = mapObjRef.current

        if (polygonModeEnabled) 
        {
            // REMOVE single-click listener
            if (clickListenerRef.current) 
            {
                unByKey(clickListenerRef.current)
                clickListenerRef.current = null
            }

            // ADD polygon draw interaction
            if (!drawInteractionRef.current) 
            {
                const vectorSource = new VectorSource()
                const vectorLayer = new VectorLayer({ source: vectorSource })
                map.addLayer(vectorLayer)
                polygonLayerRef.current = vectorLayer

                const draw = new Draw({
                    source: vectorSource,
                    type: "Polygon",
                })
                map.addInteraction(draw)
                drawInteractionRef.current = draw

                draw.on("drawend", (evt: any) => {
                    const drawnFeature = evt.feature;
                    const drawnGeometry = drawnFeature.getGeometry();
                    const coords: [number, number][] = evt.feature.getGeometry().getCoordinates()
                    //setCoordinates(prevState => [...prevState, coords])
                    //console.log("Polygon coordinates:", coords)
                    //setPolygonPopup(true)
                    rowCoordinateRef.current = [coords]

                    // Get all intersecting features from your GeoJSON layers
                    const intersectingFeatures: Feature<Geometry>[] = [];
                    
                    // Get the extent of the drawn polygon for quick filtering
                    const drawnExtent = drawnGeometry.getExtent();
                    
                    // Loop through all vector layers (your GeoJSON layers)
                    map.getLayers().getArray().forEach(layer => {
                        // Skip the drawing layer itself AND non-visible layers
                        if (layer === vectorLayer || !(layer instanceof VectorLayer) || !layer.getVisible()) return;
                        
                        const source = layer.getSource();
                        if (source && source instanceof VectorSource) {
                            // First filter by extent for performance
                            const featuresInExtent = source.getFeaturesInExtent(drawnExtent);
                            
                            featuresInExtent.forEach((feature: Feature<Geometry>) => {
                                const featureGeometry = feature.getGeometry();
                                if (!featureGeometry) return;
                                
                                // Check if the drawn polygon intersects with this feature
                                if (drawnGeometry.intersectsExtent(featureGeometry.getExtent())) {
                                    // For more precise intersection checking
                                    if (isFeatureIntersecting(drawnGeometry, featureGeometry)) {
                                        intersectingFeatures.push(feature);
                                    }
                                }
                            });
                        }
                    })
                    setCoordinates(prevState => [...intersectingFeatures])
                    handleSetPolygonDetails()
                })
            }
        } 
        else 
        {
            setPolygonPopup(false)
            // REMOVE polygon draw interaction
            if (drawInteractionRef.current) 
            {
                map.removeInteraction(drawInteractionRef.current)
                drawInteractionRef.current = null
            }

            if (polygonLayerRef.current) 
            {
                map.removeLayer(polygonLayerRef.current)
                polygonLayerRef.current = null
            }

            // ADD single-click listener
            if (!clickListenerRef.current) 
            {
                clickListenerRef.current = map.on("singleclick", (e) => handleMapOnClickEvent(e))
            }
        }

        // Cleanup on unmount
        return () => {
            if (clickListenerRef.current) 
            {
                unByKey(clickListenerRef.current)
                clickListenerRef.current = null
            }
            if (drawInteractionRef.current) 
            {
                map.removeInteraction(drawInteractionRef.current)
                drawInteractionRef.current = null
            }
            if (polygonLayerRef.current) 
            {
                map.removeLayer(polygonLayerRef.current)
                polygonLayerRef.current = null
            }
        }
    }, [polygonModeEnabled, mapObjRef.current])

    useEffect(() => {
        if (!polygonPopup) 
        {
            setCoordinates([])
            rowCoordinateRef.current = []
        }
    }, [polygonPopup]);

    const isFeatureIntersecting = (drawnGeometry: Geometry, featureGeometry: Geometry): boolean => {
        try {
            const drawnType = drawnGeometry.getType();
            const featureType = featureGeometry.getType();
            
            // For Polygon vs Polygon intersection
            if (drawnType === 'Polygon' && featureType === 'Polygon') {
                const drawnPoly = drawnGeometry as Polygon;
                const featurePoly = featureGeometry as Polygon;
                
                // Get interior point of the feature polygon
                const interiorPoint = featurePoly.getInteriorPoint();
                
                // Check if the interior point is within the drawn polygon
                if (drawnPoly.intersectsCoordinate(interiorPoint.getCoordinates())) {
                    return true;
                }
                
                // Also check if drawn polygon's interior point is within feature
                const drawnInteriorPoint = drawnPoly.getInteriorPoint();
                if (featurePoly.intersectsCoordinate(drawnInteriorPoint.getCoordinates())) {
                    return true;
                }
                
                return false;
            }
            
            // For Point features
            if (featureType === 'Point') {
                const pointCoords = (featureGeometry as any).getCoordinates();
                return drawnGeometry.intersectsCoordinate(pointCoords);
            }
            
            // For MultiPolygon features
            if (featureType === 'MultiPolygon') {
                const multiPoly = featureGeometry as MultiPolygon;
                const polygons = multiPoly.getPolygons();
                
                for (const polygon of polygons) {
                    const interiorPoint = polygon.getInteriorPoint();
                    if (drawnGeometry.intersectsCoordinate(interiorPoint.getCoordinates())) {
                        return true;
                    }
                }
                return false;
            }
            
            // Fallback: check extent intersection
            return drawnGeometry.intersectsExtent(featureGeometry.getExtent());
            
        } catch (error) {
            console.error('Error checking intersection:', error);
            return false;
        }
    }

    const handleSetPolygonDetails = () => {
        setPolygonPopup(true)
    }

    useEffect(() => {
        if (!polygonModeEnabled) 
        {
            setCoordinates([])
        }
    }, [polygonModeEnabled])

    const handlePolygonModeEnableClick = () => {
        if (!polygonModeEnabled) 
        {
            setCoordinates([]);
            rowCoordinateRef.current = [];
        }
        setPolygonModeEnabled(prevState => !prevState)
    }

    useEffect(() => {
        if (!mapObjRef.current || !vectorLayers.length || !props.allowedAreas?.length) return
    
        const colors = getThemeColors(theme)
        const map = mapObjRef.current
        
        // Get all layers
        const allLayers = map.getLayers().getArray()
        
        allLayers.forEach(layer => {
            if (layer instanceof VectorLayer) {
                const source = layer.getSource()
                if (!source) return
                
                source.getFeatures().forEach((feature: Feature<Geometry>) => {
                    // Get the feature's admin code
                    const featureCode = feature.get('ADM3_PCODE') || 
                                       feature.get('ADM2_PCODE') || 
                                       feature.get('ADM1_PCODE') || 
                                       feature.get('ADM0_PCODE')
                    
                    if (!featureCode) return
                    
                    // Check if this feature's code starts with any allowed area
                    const isAllowed = props.allowedAreas.some(allowed => 
                        featureCode.startsWith(allowed) || allowed.startsWith(featureCode)
                    )
                    
                    if (!isAllowed) {
                        // Disable the feature
                        feature.setStyle(new Style({
                            fill: new Fill({ color: colors.disable }),
                            stroke: new Stroke({ color: colors.disableStroke, width: 1 })
                        }))
                        feature.set('interactive', false)
                        
                        // Also disable any child features
                        allLayers.forEach(childLayer => {
                            if (childLayer instanceof VectorLayer) {
                                const childSource = childLayer.getSource()
                                if (!childSource) return
                                
                                childSource.getFeatures().forEach((childFeature: Feature<Geometry>) => {
                                    const childFeatureCode = childFeature.get('ADM3_PCODE') || 
                                                            childFeature.get('ADM2_PCODE') || 
                                                            childFeature.get('ADM1_PCODE') || 
                                                            childFeature.get('ADM0_PCODE')
                                    
                                    if (childFeatureCode && childFeatureCode.startsWith(featureCode)) {
                                        childFeature.setStyle(new Style({
                                            fill: new Fill({ color: colors.disable }),
                                            stroke: new Stroke({ color: colors.disableStroke, width: 1 })
                                        }))
                                        childFeature.set('interactive', false)
                                    }
                                })
                            }
                        })
                        
                        // Remove from selected features if it was selected
                        setSelectedFeatures(prevSelected => 
                            prevSelected.filter(selected => {
                                const selectedCode = selected.get('ADM3_PCODE') || 
                                                    selected.get('ADM2_PCODE') || 
                                                    selected.get('ADM1_PCODE') || 
                                                    selected.get('ADM0_PCODE')
                                return selectedCode !== featureCode
                            })
                        );
                        
                        // Remove from table data if present
                        props.setTableData(prevState => 
                            prevState.filter(item => item.data.code !== featureCode)
                        )
                    }
                })
            }
        })
    }, [props.allowedAreas, mapObjRef.current, vectorLayers, theme])

    return (
        <div style={{position: 'relative'}}>
            <div ref={mapRef} style={{ width: '100%', height: '600px', borderRadius: '0.5rem', backgroundColor: 'var(--color-main-black)', overflow: 'hidden'}}></div>
            {/* <div className='button-wrapper'>
                <button onClick={(e) => handleGenerateEWMessageData()}>generate EW message data</button>
            </div> */}
            {/* <div style={{display: 'flex', flexDirection: 'column'}}>
                {selectedFeatures.map((item, index) => (
                    <span key={index}>Admin 0: {item.getProperties()['ADM0_EN'] || ' '}, Admin 1: {item.getProperties()['ADM1_EN'] || ' '}, Admin 2: {item.getProperties()['ADM2_EN'] || ' '}, Admin 3: {item.getProperties()['ADM3_EN'] || ' '}</span>
                ))}
            </div> */}

            {popupStatus && (
                <MapSelectorPopup type={props.type} setPopupStatus={setPopupStatus} data={latestSelect !== null ? latestSelect : lastSelected} onDeselect={handleDeselectFeature} setData={props.setData} startDate={props.startDate} endDate={props.endDate} tableData={props.tableData} setTableData={props.setTableData}/>
            )}

            <div className='map-level-indicator'>
                <span className='title'>Area Selection Level</span>
                <span className='name'>{adminLevel}</span>
            </div>

            <button className='draw-type-button' style={{ backgroundColor: polygonModeEnabled ? 'var(--color-main-red)' : 'var(--color-main-green)'}} onClick={(e) => handlePolygonModeEnableClick()}>{polygonModeEnabled ? 'End Draw Mode' : 'Start Draw Mode'}</button>

            {polygonPopup && (
                <MapSelectorPolygonPopup type={props.type} setPopupStatus={setPolygonPopup} data={coordinates !== null ? coordinates : coordinates} onDeselect={handleDeselectFeatureMulti} setData={props.setData} startDate={props.startDate} endDate={props.endDate} tableData={props.tableData} setTableData={props.setTableData}/>
            )}

            {/* {coordinates.length > 0 && !polygonPopup && (
                <button className="set-detail-button" onClick={(e) => handleSetPolygonDetails()}>Set Details</button>
            )} */}
            
        </div>
    )
}

export default MapSelector